<template>
    <div>
        <div v-if="customerHasPaymentMethod" class="payment-method-info">
            <p class="payment-method-title">{{ $t('cart.stripe.payment_method') }}: {{ paymentMethod.type }}</p>
            <div v-if="customerHasCard">
                <p>{{ $t('cart.stripe.card_last_digits') }}: **** **** **** {{ paymentMethod.card.last4 }}</p>
                <p>{{ $t('cart.stripe.card_brand') }}: {{ paymentMethod.card.brand }}</p>
                <p>{{ $t('cart.stripe.card_exp_month') }}: {{ paymentMethod.card.exp_month }}</p>
                <p>{{ $t('cart.stripe.card_exp_year') }}: {{ paymentMethod.card.exp_year }}</p>
            </div>
        </div>

        <div id="payment-element"></div>
        <div class="button-group">
            <button type="button" @click="handleSubmit">{{ customerHasPaymentMethod ?  $t('cart.stripe.update_token') : $t('cart.stripe.create_token') }}</button>
            <button type="button" @click="handleRemove" v-if="customerHasPaymentMethod" class="remove-button"
                :title="$t('cart.stripe.remove_payment_method')">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                    class="bi bi-trash" viewBox="0 0 16 16">
                    <path
                        d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                    <path
                        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';

export default {
    name: 'ChangePaymentStripe',
    data() {
        return {
            stripe: null,
            elements: null,
            paymentElement: null,
            customer: null,
            publicKey: '',
            customerHasPaymentMethod: false,
            customerHasCard: false,
            paymentMethod: null,
            setupIntent: null,
            paymentMethodType: '',
            options: {
                mode: 'setup',
                layout: {
                    type: 'tabs',
                    defaultCollapsed: false,
                },
            },
            appearance: {
                theme: 'stripe',
            },
            confirmParams: {
                return_url: window.location.href,
                if_required: false,
            },
        };
    },
    async mounted() {
        await this.fetchPublicKey();
        await this.getOrCreateCustomer();
        this.stripe = await loadStripe(this.publicKey);
        await this.createSetupIntent();
        this.elements = this.stripe.elements({
            clientSecret: this.setupIntent.client_secret,
            appearance: this.appearance,
        });
        this.paymentElement = this.elements.create('payment', this.options);
        this.paymentElement.mount('#payment-element');
        await this.checkCustomerPaymentMethod();
        await this.paymentElement.on('change', (event) => {
            this.paymentMethodType = event.value;
        });
    },
    methods: {
        async fetchPublicKey() {
            try {
                const response = await axios.get('/api/gateways/stripe/public-key');
                this.publicKey = response.data.publicKey;
            } catch (error) {
                console.error('Erro ao buscar a chave pública:', error);
            }
        },
        async getOrCreateCustomer() {
            try {
                const response = await axios.post('/api/gateways/stripe/customer');
                this.customer = response.data.customer;
            } catch (error) {
                console.error(error);
            }
        },
        async createSetupIntent() {
            try {
                const response = await axios.post('/api/gateways/stripe/setup-intent', {
                    customerId: this.customer.id,
                });
                this.setupIntent = response.data.setupIntent;
            } catch (error) {
                console.error('Erro ao criar Setup Intent:', error);
            }
        },
        async handleSubmit() {
            try {
                const { error: submitError } = await this.elements.submit();

                if (submitError) {
                    this.$notify({
                        type: 'danger',
                        message: this.$t(`payment_erros.stripe.${submitError.code}`),
                    });
                    return;
                }

                const { setupIntent, error } = await this.stripe.confirmSetup({
                    elements: this.elements,
                    clientSecret: this.setupIntent.client_secret,
                    confirmParams: {
                        return_url: window.location.href,
                    },
                    redirect: 'if_required',
                });

                if (error) {
                    this.$notify({
                        type: 'danger',
                        message: this.$t(`payment_erros.stripe.${error.code}`),
                    });
                    return;
                }

                // Verifique se setupIntent está pronto e obtenha o payment_method
                if (setupIntent && setupIntent.payment_method) {
                // Envie o payment_method para o backend para armazenamento
                    const response = await axios.post('/api/gateways/stripe/payment-method', {
                        customerId: this.customer.id,
                        paymentMethodId: setupIntent.payment_method,
                    });

                    if (response.data.paymentMethod) {
                        this.updateCustomerCard(response.data.paymentMethod);
                    } else {
                        this.$notify({
                            type: 'danger',
                            message: response.data.error,
                        });
                    }
                }
            } catch (error) {
                this.$notify({
                    type: 'danger',
                    message: error.message,
                });
            }
        },
        async handleRemove() {
            try {
                const response = await axios.delete(`/api/gateways/stripe/payment-method/${this.paymentMethod.id}`);
                if (response.data.paymentMethod) {
                    this.removeCustomerCard();
                } else {
                    this.$notify({
                        type: 'danger',
                        message: response.data.error,
                    });
                    console.error('Erro ao remover o Payment Method:', response.data.error);
                }
            } catch (error) {
                this.$notify({
                    type: 'danger',
                    message: error.message,
                });
                console.error('Erro ao remover o Payment Method:', error);
            }
        },
        async checkCustomerPaymentMethod() {
            try {
                const response = await axios.get(`/api/gateways/stripe/payment-method/${this.customer.id}`);
                this.paymentMethod = response.data.paymentMethod;
                if (this.paymentMethod) {
                    if (this.paymentMethod.type === 'card') {
                        this.customerHasCard = true;
                    }
                    this.paymentMethodType = this.paymentMethod.type;
                    this.customerHasPaymentMethod = true;
                }
            } catch (error) {
                this.$notify({
                    type: 'danger',
                    message: error.message,
                });
                console.error('Erro ao verificar o cartão do cliente:', error);
            }
        },
        updateCustomerCard(paymentMethod) {
            this.paymentMethod = paymentMethod;
            this.customerHasPaymentMethod = true;
            if (paymentMethod.type === 'card') {
                this.customerHasCard = true;
            }
        },
        removeCustomerCard() {
            this.paymentMethod = null;
            this.customerHasPaymentMethod = false;
            this.customerHasCard = false;
        },
    },

};
</script>

<style scoped>
.button-group {
    display: flex;
    justify-content: space-between;
}

button {
    background-color: #6772e5;
    color: white;
    border: none;
    padding: 10px 15px;
    font-size: 16px;
    border-radius: 4px;
    cursor: pointer;
    margin-top: 10px;
    margin-bottom: 10px;
    transition: background-color 0.3s ease;
}

button:hover {
    background-color: #5469d4;
}

.remove-button {
    background-color: #e53935;
    margin-left: 10px;
}

.remove-button:hover {
    background-color: #d32f2f;
}

.payment-method-info {
    background-color: #f6f9fc;
    border: 1px solid #e1e4e8;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 20px;
}

.payment-method-info p {
    margin: 0;
    font-size: 14px;
    color: #333;
}

.payment-method-title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 10px;
}
</style>
